import { StreamPartIDUtils, executeSafePromise } from '@streamr/utils';
import { createLazyMap } from '../../utils/Mapping';
import { PushBuffer } from '../../utils/PushBuffer';
import { GapFiller } from './GapFiller';
import { OrderedMessageChain } from './OrderedMessageChain';
const createMessageChain = (context, getStorageNodes, onUnfillableGap, resends, config, abortSignal) => {
    const resend = async function* (gap, storageNodeAddress, abortSignal) {
        const msgs = await resends.resend(context.streamPartId, {
            from: {
                timestamp: gap.from.getMessageRef().timestamp,
                sequenceNumber: gap.from.getMessageRef().sequenceNumber + 1,
            },
            to: gap.to.prevMsgRef,
            publisherId: context.publisherId,
            msgChainId: context.msgChainId,
            raw: true
        }, async () => [storageNodeAddress], abortSignal);
        yield* msgs;
    };
    const chain = new OrderedMessageChain(context, abortSignal);
    chain.on('unfillableGap', (gap) => onUnfillableGap(gap));
    const gapFiller = new GapFiller({
        chain,
        resend,
        getStorageNodeAddresses: () => getStorageNodes(StreamPartIDUtils.getStreamID(context.streamPartId)),
        strategy: config.gapFillStrategy,
        initialWaitTime: config.gapFillTimeout,
        retryWaitTime: config.retryResendAfter,
        maxRequestsPerGap: (config.gapFill) ? config.maxGapRequests : 0,
        abortSignal
    });
    gapFiller.start();
    return chain;
};
/**
 * Manages message ordering and gap filling (per stream part). Provides an iterator
 * to read the sequence of messages which are in ascending order and gaps are filled
 * (if enabled, and the missing message available in a storage node)
 */
export class OrderMessages {
    chains;
    outBuffer = new PushBuffer();
    abortController = new AbortController();
    constructor(streamPartId, getStorageNodes, onUnfillableGap, resends, config) {
        this.chains = createLazyMap({
            valueFactory: async ([publisherId, msgChainId]) => {
                const chain = createMessageChain({
                    streamPartId,
                    publisherId,
                    msgChainId
                }, getStorageNodes, onUnfillableGap, resends, config, this.abortController.signal);
                chain.on('orderedMessageAdded', (msg) => this.onOrdered(msg));
                return chain;
            }
        });
    }
    onOrdered(orderedMessage) {
        if (!this.outBuffer.isDone()) {
            executeSafePromise(() => this.outBuffer.push(orderedMessage));
        }
    }
    destroy() {
        this.outBuffer.endWrite();
        this.abortController.abort();
    }
    async addMessages(src) {
        try {
            for await (const msg of src) {
                if (this.abortController.signal.aborted) {
                    return;
                }
                const chain = await this.chains.get([msg.getPublisherId(), msg.getMsgChainId()]);
                chain.addMessage(msg);
            }
            await Promise.all([...this.chains.values()].map((chain) => chain.waitUntilIdle()));
            this.outBuffer.endWrite();
        }
        catch (err) {
            this.outBuffer.endWrite(err);
        }
    }
    [Symbol.asyncIterator]() {
        return this.outBuffer;
    }
}
